import { applyPageMetadata } from "@/lib/page-metadata";
import { LastUpdated } from "@/components/site/LastUpdated";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { GlossaryAuthor } from "@/components/site/GlossaryAuthor";
import { getTermBySlug, type GlossaryTerm } from "@/data/glossary";
import { SITE_URL, jsonLdScript } from "@/lib/seo";

export const Route = createFileRoute("/glossary/$slug")({
  loader: ({ params }) => {
    const term = getTermBySlug(params.slug);
    if (!term) throw notFound();
    return { term };
  },
  head: ({ params, loaderData }) => {
    const t = loaderData?.term;
    if (!t) return { meta: [{ title: "Not found" }, { name: "robots", content: "noindex" }] };
    const url = `${SITE_URL}/glossary/${params.slug}`;
    const title = `What Is ${t.term}? | Law Firm SEO Glossary`;
    return applyPageMetadata(`/glossary/${params.slug}`, {
      meta: [
        { title },
        { name: "description", content: t.metaDescription },
        { property: "og:title", content: title },
        { property: "og:description", content: t.metaDescription },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: t.metaDescription },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        jsonLdScript({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "DefinedTerm",
              "@id": `${url}#term`,
              name: t.term,
              description: t.shortDefinition,
              url,
              inDefinedTermSet: `${SITE_URL}/glossary#termset`,
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
                { "@type": "ListItem", position: 2, name: "Glossary", item: `${SITE_URL}/glossary` },
                { "@type": "ListItem", position: 3, name: t.term, item: url },
              ],
            },
          ],
        }),
      ],
    });
  },
  component: TermPage,
});

function Sidebar({ t }: { t: GlossaryTerm }) {
  const related = t.relatedTerms.map((s) => getTermBySlug(s)).filter(Boolean) as GlossaryTerm[];
  return (
    <div className="space-y-5">
      <div className="rounded-xl bg-hero-gradient text-primary-foreground p-6">
        <h2 className="font-display text-xl font-semibold mb-4">Need help with this?</h2>
        <a href={t.service.href} className="inline-flex w-full items-center justify-center rounded-md bg-[var(--gold)] text-[var(--navy-deep)] px-5 py-3 text-sm font-semibold text-center hover:bg-[var(--gold-deep)] transition">{t.service.label}</a>
      </div>
      {related.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="font-display text-xl font-semibold mb-3 text-foreground">Related terms</h2>
          <ul className="divide-y divide-border">
            {related.map((r) => (
              <li key={r.slug}><Link to="/glossary/$slug" params={{ slug: r.slug }} className="block py-2.5 text-sm font-medium text-primary hover:text-[var(--gold-deep)]">{r.term}</Link></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TermPage() {
  const { term: t } = Route.useLoaderData();
  const h2 = "font-display text-2xl sm:text-3xl font-semibold text-foreground mt-10 mb-4";
  return (
    <Layout>
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-5 pt-10 pb-24 sm:px-6 sm:pt-14 sm:pb-28">
          <nav aria-label="Breadcrumb" className="mb-5 text-xs text-primary-foreground/70">
            <ol className="flex flex-wrap items-center gap-1">
              <li><a href="/" className="hover:text-[var(--gold)]">Home</a></li>
              <li>/</li>
              <li><Link to="/glossary" className="hover:text-[var(--gold)]">Glossary</Link></li>
              <li>/</li>
              <li className="text-primary-foreground">{t.term}</li>
            </ol>
          </nav>
          <Link to="/glossary" search={{ category: t.category }} className="inline-block rounded-full border border-[var(--gold)]/50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[var(--gold)] hover:bg-white/10 mb-4">{t.category}</Link>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold max-w-3xl">What Is {t.term}?</h1><LastUpdated />
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 sm:px-6 -mt-14 relative z-10 pb-16 grid gap-10 lg:grid-cols-[minmax(0,780px)_320px] lg:justify-between">
        <article className="min-w-0">
          <div className="rounded-xl border border-border bg-card p-6 sm:p-8 shadow-[var(--shadow-elegant)]">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)] mb-3">Definition</p>
            <p className="font-display text-xl sm:text-2xl leading-relaxed text-foreground">{t.shortDefinition}</p>
          </div>

          <h2 className={h2}>Why it matters for law firms</h2>
          <p className="text-foreground/85 leading-relaxed text-lg">{t.whyItMatters}</p>

          <h2 className={h2}>Example</h2>
          <div className="rounded-xl bg-muted p-6 text-foreground/85 leading-relaxed text-lg">{t.lawFirmExample}</div>

          <h2 className={h2}>How to improve it</h2>
          <ul className="list-disc pl-6 space-y-2 text-foreground/85 leading-relaxed text-lg marker:text-[var(--gold-deep)]">
            {t.howToImprove.map((x, i) => <li key={i}>{x}</li>)}
          </ul>

          <div className="mt-10 lg:hidden"><Sidebar t={t} /></div>

          {t.blog && (
            <>
              <h2 className={h2}>Further reading</h2>
              <a href={t.blog.href} className="text-lg font-semibold text-primary underline decoration-[var(--gold)] underline-offset-4 hover:text-[var(--gold-deep)]">{t.blog.label}</a>
            </>
          )}

          <div className="mt-12"><GlossaryAuthor /></div>
          <Link to="/glossary" className="mt-8 inline-block text-sm font-semibold text-primary hover:text-[var(--gold-deep)]">← Back to glossary</Link>
        </article>
        <aside className="hidden lg:block pt-20">
          <div className="sticky top-28"><Sidebar t={t} /></div>
        </aside>
      </div>
    </Layout>
  );
}
